import { motion } from "framer-motion";
import { tempMeta, initials, fullName, fmtStamp, timeAgo } from "../lib/constants";
import { fileUrl } from "../lib/api";
import { Loader2, Inbox, UserRound, PencilLine } from "lucide-react";

export function Stamp({ by, at, prefix, icon: Icon = UserRound, className = "", testid }) {
  if (!by && !at) return null;
  return (
    <span data-testid={testid} title={at ? new Date(at).toLocaleString() : undefined}
      className={`inline-flex items-center gap-1 text-[11px] leading-none text-slate-400 whitespace-nowrap ${className}`}>
      <Icon className="w-3 h-3 shrink-0" />
      {prefix && <span>{prefix}</span>}
      {by && <span className="font-medium text-slate-500">{by}</span>}
      {by && at && <span>·</span>}
      {at && <span>{fmtStamp(at)}</span>}
      {at && <span className="text-slate-300">· {timeAgo(at)}</span>}
    </span>
  );
}

export function RecordMeta({ doc, className = "", testid = "record-meta" }) {
  if (!doc) return null;
  const createdBy = doc.created_by_name || doc.uploaded_by_name || doc.author;
  const createdAt = doc.created_at || doc.timestamp;
  const changed = doc.updated_at && createdAt && Math.abs(new Date(doc.updated_at) - new Date(createdAt)) > 1500;
  return (
    <div data-testid={testid} className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
      <Stamp prefix="Created by" by={createdBy || "System"} at={createdAt} testid={`${testid}-created`} />
      {changed && <Stamp prefix="Updated by" by={doc.updated_by_name || "System"} at={doc.updated_at} icon={PencilLine} testid={`${testid}-updated`} />}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8" data-testid={testid}>
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy-800">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "navy", sub, onClick, testid }) {
  const accents = {
    navy: "text-navy-700 bg-navy-800/5",
    gold: "text-gold-dark bg-amber-500/10",
    red: "text-red-600 bg-red-500/10",
    green: "text-emerald-600 bg-emerald-500/10",
    blue: "text-sky-600 bg-sky-500/10",
  };
  return (
    <motion.div
      whileHover={{ y: -3 }}
      onClick={onClick}
      data-testid={testid}
      className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-amber-500/40 transition-[box-shadow,border-color] ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          <p className="font-display text-2xl sm:text-3xl font-bold text-navy-800 mt-2">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </div>
        {Icon && <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}><Icon className="w-5 h-5" /></div>}
      </div>
    </motion.div>
  );
}

export function TempBadge({ value }) {
  const t = tempMeta(value);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${t.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />{t.label}
    </span>
  );
}

export function Pill({ children, className = "" }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600 ${className}`}>{children}</span>;
}

export function Avatar({ contact, size = 40 }) {
  const style = { width: size, height: size };
  if (contact?.photo_file_id) {
    return <img src={fileUrl(contact.photo_file_id)} alt={fullName(contact)} style={style} className="rounded-full object-cover ring-2 ring-white shadow" />;
  }
  if (contact?.picture) {
    return <img src={contact.picture} alt={fullName(contact)} style={style} className="rounded-full object-cover ring-2 ring-white shadow" />;
  }
  return (
    <div style={style} className="rounded-full bg-gradient-to-br from-navy-700 to-navy-900 text-gold font-display font-bold flex items-center justify-center ring-2 ring-white shadow text-sm">
      {initials(contact)}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="loading-state">
      <Loader2 className="w-7 h-7 animate-spin mb-3" />
      <p className="text-sm">{label}…</p>
    </div>
  );
}

export function Empty({ title = "Nothing here yet", subtitle, action, testid }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid={testid || "empty-state"}>
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4"><Inbox className="w-7 h-7 text-slate-400" /></div>
      <p className="font-display text-lg font-semibold text-navy-800">{title}</p>
      {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function SectionCard({ title, action, children, className = "", testid }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display font-semibold text-navy-800">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}
